// The `GiftVoucher` component provides a comprehensive interface for creating and purchasing gift vouchers.
// It displays service details, including images optimized for desktop and mobile, and offers dropdowns and
// input fields to collect necessary information such as recipient details, email, gift amount, and type.
// It includes validation for required fields and ensures email input is in the correct format. Upon form
// submission, the data is processed into a payload and added to the cart using the `addItem` function.
// Additionally, the component highlights delivery instructions, policy details, and a customizable call-to-action
// button for seamless user interaction.

import { useCart } from 'frontastic';
import { getCurrencyCookie } from 'helpers/utils/getCurrencyCookie';
import React, { useState } from 'react';
import { DropdownOption, GiftVoucherService, InputBox } from 'types/next';

type ContentDataProps = {
  contentData: GiftVoucherService;
};
type Errors = Record<string, string>;
type SelectedValues = Record<string, string>;

const extractAmount = (text: string): number | null => {
  const match = text.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

// const labelToKeyMap: { [key: string]: string } = {
//   'Name of the person the gift is for': 'recipient',
//   'Which email should we send this E-Voucher to': 'recipientemail',
//   'How much do you wish to spend?': 'amount',
//   'Gift Type': 'type',
// };

const GiftVoucher = ({ contentData }: ContentDataProps) => {
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const { addItem } = useCart();
  const localeSelection = getCurrencyCookie('locale_selection');
  const currency: string = localeSelection?.currency || null;

  // Helper function to get the correct options based on currency
  const getOptions = (dropdown: DropdownOption) => {
    if (currency === 'EUR' && dropdown?.['options-en-EU']) {
      return dropdown?.['options-en-EU']; // Use EU options for EUR currency
    }
    return dropdown?.options; // Default to GBP options
  };

  const validateField = (label: string, value: string, type?: string) => {
    let error = '';

    // Function to check if an object is empty
    const isEmptyObject = (obj: any) => obj && typeof obj === 'object' && Object.keys(obj)?.length === 0;

    // Check for invalid values (empty string, null, undefined, empty object)
    if (
      !label ||
      isEmptyObject(label) ||
      !value ||
      isEmptyObject(value) ||
      value === '--Please Select--' ||
      (typeof value === 'string' && !value.trim())
    ) {
      error = 'This field is required.';
    } else if (type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        error = 'Invalid email format';
      }
    }
    setErrors((prev) => ({ ...prev, [label]: error }));
    return error === '';
  };

  const handleDropdownChange = (label: string, value: string) => {
    // const key = labelToKeyMap[label] || label; // Map label to key

    const updatedValue = label === 'How much do you wish to spend?' ? extractAmount(value) : value;

    setSelectedValues((prev: any) => ({
      ...prev,
      [label]: updatedValue,
    }));
    validateField(label, value);
  };

  const handleInputChange = (label: string, value: string, type?: string) => {
    // const key = labelToKeyMap[label] || label; // Map label to key
    setInputValues((prev) => ({
      ...prev,
      [label]: value,
    }));
    validateField(label, value, type);
  };

  const validateInputs = () => {
    let isValid = true;
    const newErrors: Errors = {};

    //validation of inputfields
    contentData?.inputBoxes?.forEach((input: InputBox) => {
      // const key = labelToKeyMap[input.question] || input.question; // Map label to key
      if (input?.required && !inputValues?.[input?.question]?.trim()) {
        newErrors[input.question] = 'This field is required.';
        isValid = false;
      } else if (input.type === 'email' && inputValues?.[input?.question]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inputValues?.[input?.question])) {
          newErrors[input.question] = 'Invalid email format';
        }
      }
    });

    //validation of dropdown field

    contentData?.dropdowns?.forEach((dropdown: DropdownOption) => {
      if (
        dropdown?.required &&
        (!selectedValues?.[dropdown?.question] || selectedValues?.[dropdown?.question] === '-- Please select --')
      ) {
        newErrors[dropdown.question] = 'This field is required.';
        isValid = false;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async () => {
    const isValid = validateInputs();
    if (!isValid) {
      console.log('Please fill in all the required fields.');
      return;
    }
    // if (validateInputs()) {
    const payload = {
      sku: 'giftcards-evouchers',
      count: 1,
      amount: selectedValues['How much do you wish to spend?'],
      type: selectedValues['Gift Type'] === 'Posted' ? 'PRINTED' : 'ONLINE',
      recipient: inputValues['Name of the person the gift is for'],
      recipientemail: inputValues['Which email should we send this E-Voucher to'],
      cid: 'giftcards-evouchers',
    };

    try {
      await addItem(payload, 1);
      console.log('Item added successfully!');
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const desktopImage = contentData?.DesktopCardImage;
  const mobileImage = contentData?.mobileCardImage;

  return (
    <>
      <div className="flex justify-center">
        <h1 className="text-[29px] font-bold md:text-[33px] lg:text-[36px]">{contentData?.serviceName}</h1>
      </div>
      <div className="mb-30 grid grid-cols-1 gap-x-30 gap-y-10 lg:grid-cols-2">
        <div className="flex justify-center">
          <picture>
            <source
              srcSet={`https://${mobileImage?.defaultHost}/i/${mobileImage?.endpoint}/${mobileImage?.name}`}
              media="(max-width: 768px)"
            />
            <img
              src={`https://${desktopImage?.defaultHost}/i/${desktopImage?.endpoint}/${desktopImage?.name}`}
              alt="giftcard"
              className=" mx-auto block h-auto w-full max-w-full md:max-w-none lg:max-w-none"
            />
          </picture>
        </div>
        <div className="flex w-full flex-col items-start gap-y-10">
          <h3 className="font-body text-16 font-normal leading-7">{'Order your gift'}</h3>

          {/* Dropdowns */}
          {contentData?.dropdowns &&
            contentData?.dropdowns?.length > 0 &&
            contentData?.dropdowns?.map((dropdown: DropdownOption, index: number) => {
              const hasError = errors?.[dropdown?.question];
              const options = getOptions(dropdown);

              return (
                <div key={index} className="my-4 w-full">
                  <label
                    htmlFor={`dropdown-${index}`}
                    className="block pb-5 text-14 font-medium leading-[17px] text-[#4b4b4b]"
                  >
                    {dropdown?.question}
                    {dropdown?.required && <span className="ml-1 text-[#EB0E0E]">*</span>}
                  </label>
                  <div className="relative w-full">
                    <select
                      id={`dropdown-${index}`}
                      className={`m-0 h-40 w-full appearance-none rounded-md border px-14 py-0
                      ${hasError ? 'border-2 border-[#EB0E0E] text-[#EB0E0E]' : 'border-gray-300'}
                      bg-[length:24px_24px] bg-[right_10px_center] bg-no-repeat text-[#4b4b4b]`}
                      style={{
                        backgroundImage: `url(\"data:image/svg+xml;charset=UTF-8,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 1L6 6L1 1' stroke='${
                          hasError ? '%23EB0E0E' : '%2300A000'
                        }' stroke-width='2'/%3E%3C/svg%3E\")`,
                        backgroundSize: '12px 12px',
                      }}
                      onChange={(e) => handleDropdownChange(dropdown?.question, e.target.value)}
                      onFocus={() => setFocusedField(dropdown?.question)}
                      onBlur={(e) => {
                        validateField(dropdown?.question, e.target.value);
                        setFocusedField(null);
                      }}
                    >
                      {options?.map((option: string, idx: number) => (
                        <option key={idx} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  {hasError && <p className="mt-1 text-sm text-[#EB0E0E]">{hasError}</p>}
                </div>
              );
            })}

          {/* Input fields */}
          {contentData?.inputBoxes &&
            contentData?.inputBoxes?.length > 0 &&
            contentData?.inputBoxes?.map((input: InputBox, index: number) => (
              <div key={index} className="relative my-4 w-full">
                <label
                  htmlFor={`input-${index}`}
                  className="block pb-2 text-14 font-medium leading-[17px] text-[#4b4b4b]"
                >
                  {input?.question}
                  {input?.required && <span className="ml-1 text-[#EB0E0E]">*</span>}
                </label>

                {/* Error icon (only shown if there's an error) */}
                {errors?.[input?.question] && <span className="absolute right-0 top-0 text-14 text-[#EB0E0E]">✖</span>}

                <input
                  id={`input-${index}`}
                  type={input?.type}
                  value={inputValues?.[input?.question] || ''}
                  onChange={(e) => handleInputChange(input?.question, e.target.value)}
                  onBlur={(e) => validateField(input?.question, e.target.value, input?.type)}
                  className={`h-40 w-full rounded-md border py-1 pl-15 pr-50 md:px-14 lg:px-14 
        ${errors?.[input?.question] ? 'border-2 border-[#EB0E0E]' : 'border-gray-300'}`}
                />

                {errors?.[input?.question] && (
                  <p className="mt-1 text-sm text-[#EB0E0E]">{errors?.[input?.question]}</p>
                )}
              </div>
            ))}

          <button
            onClick={handleSubmit}
            className="mt-6 h-40 w-full rounded-md bg-[#1dce8f] px-4 py-2 font-body text-14 font-bold uppercase leading-5 text-black"
          >
            {contentData?.button?.label}
          </button>
        </div>
      </div>
      <div className="">
        <p className="text-16 font-normal leading-[19.2px]">{contentData?.voucherDeliveryPolicy}</p>
      </div>
      <hr className="my-28" />
    </>
  );
};

export default GiftVoucher;
